<template>
  <div class="loading-container" :class="containerClass">
    <div class="loading-content">
      <!-- Rotating Company Logo -->
      <img 
        src="/loading.png" 
        alt="Loading..." 
        class="loading-spinner"
        :class="spinnerClass"
      />
      <p v-if="message" class="loading-message" :class="messageClass">
        {{ message }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  message?: string
  size?: 'small' | 'medium' | 'large'
  overlay?: boolean
  darkMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  message: '',
  size: 'medium',
  overlay: false,
  darkMode: false
})

const containerClass = computed(() => {
  const classes = []
  
  if (props.overlay) {
    classes.push('loading-overlay')
  } else {
    classes.push('loading-inline')
  }
  
  if (props.darkMode) {
    classes.push('loading-dark')
  }
  
  return classes.join(' ')
})

const spinnerClass = computed(() => {
  const classes = ['animate-spin']
  
  switch (props.size) {
    case 'small':
      classes.push('h-8 w-8')
      break
    case 'large':
      classes.push('h-20 w-20')
      break
    default: // medium
      classes.push('h-12 w-12')
      break
  }
  
  return classes.join(' ')
})

const messageClass = computed(() => {
  const classes = ['text-sm', 'mt-3', 'font-medium']
  
  if (props.darkMode) {
    classes.push('text-gray-300')
  } else {
    classes.push('text-gray-600')
  }
  
  return classes.join(' ')
})
</script>

<style scoped>
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
  z-index: 1000;
}

.loading-overlay.loading-dark {
  background-color: rgba(26, 26, 26, 0.9);
}

.loading-inline {
  padding: 2rem;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  object-fit: contain;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
}

.loading-message {
  text-align: center;
  max-width: 300px;
}

/* Custom animation for smooth rotation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 2s linear infinite;
}
</style>
